// Moved by `git mv` from
// `src/hooks/query/integrations/general/useQueryAvailableStandardizedSections.ts`,
// which survives as a delegate — so `git log --follow` on THIS path stops at the
// move and the pre-move history is `git log --follow` on the source path. The
// suite beside it is the repo-root `__tests__/hooks/` file relayered, and that
// one DOES trace, because its old path disappeared.
//
// THREE INPUT PORTS, NOT ONE, and the D3 record predicted exactly that: this
// was measured as "the ONLY bodied module on this door whose sole class is
// in-domain app hook", with the seam "`useIntegrationEhrInfo`, `useIsFhirAuth`
// (itself `@/store`-tethered) and `useAmdMarkdownSectionalEditorEnabled` (a flag
// getter, and flags stay app-resolved by rule)". All three resolve app-side and
// arrive as values; what crosses is the cache identity, the request and the
// V2/V2_1 selection.
//
// THE V2/V2_1 BRANCH IS NOT A TRANSPORT DECISION LEFT IN THE DELEGATE, which is
// what the D3 record expected it to be ("its delegate would hold a transport
// decision as well as three inputs"). Both endpoints are behind this door —
// `getIntegrationAvailableStandardizedSections` and its `…V21` sibling differ
// only by axios instance and are pinned side by side in `../api/note-options` —
// so the branch moves IN with the request and the delegate carries the flag
// value alone.
import { skipToken, useQuery } from '@tanstack/react-query';

import { ehrQueryKeys } from '../../contracts/queryKeys';
import {
  getIntegrationAvailableStandardizedSections,
  getIntegrationAvailableStandardizedSectionsV21,
} from '../api/note-options';

type Params = {
  /**
   * `integrationOnboardingSessionId` from `useIntegrationEhrInfo()`. Part of the
   * cache identity even when unresolved: the hook builds its key before the id
   * exists and gates the fetch with `skipToken`, so the `null`-bearing key is a
   * real entry.
   */
  integrationOnboardingSessionId: string | null | undefined;
  /**
   * `useAmdMarkdownSectionalEditorEnabled(provider)` — the AdvancedMD markdown
   * pilot flag. Chooses the V2_1 catalogue AND enters the cache identity,
   * because the two endpoints return different section shapes.
   */
  isAmdMarkdownSectionsCatalogue?: boolean;
  /**
   * `(isGenericLaunch || !isFhirAuth) && canSyncSectionalNotes`, composed
   * app-side out of `useIntegrationEhrInfo()` and `useIsFhirAuth()`.
   *
   * THE ONBOARDING-SESSION HALF OF THE GATE STAYS HERE, not in the delegate:
   * the package has the id, and keeping `Boolean(id)` beside the key that
   * carries it is what stops the two drifting apart.
   */
  enabled?: boolean;
};

/**
 * The standardized-section catalogue for the connected integration's onboarding
 * session — the list a sectional note can be written back into.
 *
 * `staleTime: Infinity` for the same reason as the Haiku catalogue: it does not
 * change inside a session and every consumer renders it in a picker.
 */
export const useQueryAvailableStandardizedSections = ({
  integrationOnboardingSessionId,
  isAmdMarkdownSectionsCatalogue = false,
  enabled = true,
}: Params) => {
  const enableQuery = enabled && Boolean(integrationOnboardingSessionId);

  return useQuery({
    queryKey: ehrQueryKeys.availableStandardizedSections({
      integrationOnboardingSessionId,
      isAmdMarkdownSectionsCatalogue,
    }),
    staleTime: Infinity,
    queryFn: enableQuery
      ? async () => {
          const onboardingSessionId = integrationOnboardingSessionId as string;
          const fetchSections = isAmdMarkdownSectionsCatalogue
            ? getIntegrationAvailableStandardizedSectionsV21
            : getIntegrationAvailableStandardizedSections;
          const { data } = await fetchSections({ onboardingSessionId });
          return data.available_sections;
        }
      : skipToken,
  });
};
